/**
 * compressPhoto — compressione client-side prima dell'upload.
 *
 * Perché:
 *  - Foto iPhone moderne pesano 8-15 MB (HEIC/JPEG 12MP)
 *  - Su 4G a 5 Mbps un upload di 10 MB richiede 16+ secondi
 *  - Per un annuncio immobiliare 2400px lato lungo a qualità 0.85 sono
 *    più che sufficienti (i portali stessi le ridimensionano)
 *  - HEIC su Safari iOS viene riconvertito automaticamente dalla lib
 *
 * Default mirati per UC immobiliare:
 *  - maxSizeMB: 2.5   → tipicamente 0.8-1.8 MB su foto reali
 *  - maxWidthOrHeight: 2400  → qualità Instagram-grade, no aliasing
 *  - useWebWorker: true (non blocca UI)
 *
 * Fallback safe: se la compressione fallisce per qualsiasi motivo
 * (browser strano, foto già piccola, libreria che esplode), torniamo
 * il File originale. Mai bloccare l'upload.
 */
import imageCompression from "browser-image-compression";

const DEFAULTS = {
  maxSizeMB: 2.5,
  maxWidthOrHeight: 2400,
  useWebWorker: true,
  fileType: "image/jpeg",  // forza JPEG anche per HEIC/PNG (più piccolo + universale)
  initialQuality: 0.85,
};

/**
 * Comprimi una foto. Skippa file < 1 MB (non vale la pena) e PDF/SVG.
 */
export async function compressPhoto(file, options = {}) {
  if (!file || typeof file !== "object") return file;
  const type = (file.type || "").toLowerCase();
  // Skip non-image
  if (type && !type.startsWith("image/")) return file;
  // Skip già piccole — niente lavoro inutile
  if (typeof file.size === "number" && file.size < 1 * 1024 * 1024) return file;
  try {
    const opts = { ...DEFAULTS, ...options };
    const compressed = await imageCompression(file, opts);
    // Mantieni il nome originale (per UX e per evitare collisioni server-side
    // se il path ha logica basata su filename)
    const finalName = file.name?.replace(/\.(heic|heif|png|webp)$/i, ".jpg") || compressed.name;
    return new File([compressed], finalName, {
      type: "image/jpeg",
      lastModified: Date.now(),
    });
  } catch (err) {
    console.warn("[compressPhoto] failed, using original:", err);
    return file;
  }
}

/**
 * Comprimi più foto in sequenza con callback di progresso.
 *
 *   onProgress(currentIdx, total, currentFileName)
 *
 * Ritorna array di File compressi (stesso ordine).
 */
export async function compressPhotos(files, onProgress) {
  const arr = Array.from(files || []);
  const out = [];
  for (let i = 0; i < arr.length; i++) {
    onProgress?.(i, arr.length, arr[i]?.name || "");
    const c = await compressPhoto(arr[i]);
    out.push(c);
  }
  onProgress?.(arr.length, arr.length, "");
  return out;
}
